import { useCallback, useEffect, useMemo, useState } from 'react'
import { Pencil, Plus, Trash2 } from 'lucide-react'
import Button from '../../components/Button'
import ConfirmDialog from '../../components/ConfirmDialog'
import DataTable from '../../components/DataTable'
import FilterBar from '../../components/FilterBar'
import PageHeader from '../../components/PageHeader'
import RowActions from '../../components/RowActions'
import { useToast } from '../../components/toastContext'
import ExamFormDrawer from './ExamFormDrawer'
import { deleteExam, getExam, getExamOptions, listExams, saveExam } from './examsService'
import { strings as S } from './strings'
import './exams.css'
import { serverDetail, serverText } from '../../lib/i18n'

/**
 * إدارة الاختبارات — the legacy `/admin/manageexams`.
 *
 * Built rather than wired (R5): nothing in the React app covered this, and
 * `/admin/manageexams` redirected to the programmes list, which is the wrong screen for an
 * exams bookmark.
 *
 * WHY IT IS SEPARATE FROM THE COURSE FORM, which already has exam settings. That form
 * writes a pass mark, attempts and three switches through `SaveCourse.ExamDetails`. It
 * cannot express how the exam is COMPOSED — "five questions from this topic at this
 * difficulty" — and those rules only exist here.
 */
export default function ExamsPage() {
  const toast = useToast()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  /* A FAILED load is not an empty one: without this the table renders its "no records"
     state for a database fault, which tells the operator the opposite of the truth. */
  const [loadError, setLoadError] = useState(null)
  const [query, setQuery] = useState('')
  const [reloadKey, setReloadKey] = useState(0)

  const [settings, setSettings] = useState(null)
  const [formOpen, setFormOpen] = useState(false)
  const [pendingDelete, setPendingDelete] = useState(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)

    listExams()
      .then((response) => {
        if (cancelled) return
        setLoadError(null)
        setRows(response.rows)
        setLoading(false)
      })
      .catch((error) => {
        if (cancelled) return
        setLoadError(error)
        setRows([])
        setLoading(false)
        toast.error(serverDetail(error?.message) || S.fail.list)
      })

    return () => {
      cancelled = true
    }
  }, [reloadKey, toast])

  /**
   * Both openers fetch first: the drawer needs the option lists positioned for the exam it
   * is about, and only the server can narrow them.
   */
  const openCreate = useCallback(async () => {
    const options = await getExamOptions().catch(() => null)
    if (!options) return toast.error(S.fail.load)
    /* The server's own sentence when there is no course left to give an exam to. */
    if (options.allCoursesHaveExam) return toast.error(serverDetail(options.message) || S.allCoursesHaveExam)

    setSettings(options)
    setFormOpen(true)
  }, [toast])

  const openEdit = useCallback(
    async (row) => {
      const full = await getExam(row.id).catch(() => null)
      if (!full) return toast.error(S.fail.load)

      setSettings(full)
      setFormOpen(true)
    },
    [toast],
  )

  const columns = useMemo(
    () => [
      {
        key: 'index',
        header: S.columns.index,
        width: '64px',
        sortable: false,
        searchable: false,
        render: (_row, index) => <span className="exams__index">{index + 1}</span>,
      },
      { key: 'course', header: S.columns.course, render: (row) => <bdi>{row.course}</bdi> },
      { key: 'name', header: S.columns.name, render: (row) => <bdi>{row.name}</bdi> },
      {
        key: 'actions',
        header: <Pencil size={15} strokeWidth={1.75} />,
        align: 'center',
        width: '112px',
        sortable: false,
        searchable: false,
        render: (row) => (
          <RowActions
            actions={[
              { id: 'edit', icon: Pencil, label: S.row.edit, onClick: () => openEdit(row) },
              {
                id: 'delete',
                icon: Trash2,
                label: S.row.delete,
                danger: true,
                onClick: () => setPendingDelete(row),
              },
            ]}
          />
        ),
      },
    ],
    [openEdit],
  )

  return (
    <>
      <PageHeader
        title={S.title}
        count={loading ? undefined : rows.length}
        actions={
          <Button onClick={openCreate}>
            <Plus size={16} strokeWidth={2} />
            {S.create}
          </Button>
        }
      />

      <FilterBar search={{ value: query, onChange: setQuery, placeholder: S.columns.name }} />

      <DataTable
        error={loadError}
        columns={columns}
        rows={rows}
        loading={loading}
        query={query}
        controls={false}
        getRowId={(row) => row.id}
        initialSort={{ key: 'course', dir: 'asc' }}
      />

      <ExamFormDrawer
        open={formOpen}
        settings={settings}
        onClose={() => setFormOpen(false)}
        onSave={async (values) => {
          const outcome = await saveExam(values)
          if (!outcome.ok) return toast.error(serverDetail(outcome.message) || S.fail.save)

          setFormOpen(false)
          toast.success(serverText(outcome.message) || S.saved)
          setReloadKey((key) => key + 1)
        }}
      />

      <ConfirmDialog
        open={!!pendingDelete}
        message={S.deleteConfirm}
        confirmLabel={S.row.delete}
        cancelLabel={S.close}
        onConfirm={async () => {
          const outcome = await deleteExam(pendingDelete.id)
          if (!outcome.ok) return toast.error(serverDetail(outcome.message) || S.fail.delete)

          setPendingDelete(null)
          toast.success(serverText(outcome.message) || S.deleted)
          setReloadKey((key) => key + 1)
        }}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  )
}
